import React, { useEffect, useRef, useState } from 'react'
import {
    BulkActionDialog,
    BulkActionDialogActions,
    BulkActionField,
    bulkActionInputClassName,
} from '../../components/ui/BulkActionDialog'
import { Callout } from '../../components/ui/callout'
import { Combobox } from '../../components/ui/combobox'
import { customFieldApiError, customFieldsApi } from './api'
import { useCustomFieldCatalog } from './useCustomFieldCatalog'
import {
    CUSTOM_FIELD_DATA_TYPES,
    CUSTOM_FIELD_ENTITY_OPTIONS,
    ENTITY_TYPE_LABELS,
    type CustomFieldDataType,
    type CustomFieldEntityType,
} from './types'

export const customFieldCreationDisabledReason = (
    definitionCount: number,
    definitionLimit: number,
): string | null =>
    definitionCount >= definitionLimit
        ? `The ${definitionLimit}-field limit has been reached for this entity type. Delete an unused field before creating another.`
        : null

export const customFieldDefinitionMutationIsCurrent = (
    request: number,
    latestRequest: number,
    requestedScope: string,
    activeScope: string,
): boolean => request === latestRequest && requestedScope === activeScope

const fieldClass = bulkActionInputClassName

export interface CustomFieldCreateModalProps {
    isOpen: boolean
    profileId: string | null
    onClose: () => void
    onCreated: () => void
}

export const CustomFieldCreateModal = ({
    isOpen,
    profileId,
    onClose,
    onCreated,
}: CustomFieldCreateModalProps) => {
    const [entityType, setEntityType] = useState<CustomFieldEntityType>('campaign')
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [dataType, setDataType] = useState<CustomFieldDataType>(CUSTOM_FIELD_DATA_TYPES.Number)
    const [error, setError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const mutationScope = `${profileId ?? 'missing'}:${entityType}`
    const activeMutationScope = useRef(mutationScope)
    const latestMutationRequest = useRef(0)
    activeMutationScope.current = mutationScope
    const { catalog, refresh: refreshQuota } = useCustomFieldCatalog(profileId, entityType)
    const creationDisabledReason = customFieldCreationDisabledReason(
        catalog?.definition_count ?? 0,
        catalog?.definition_limit ?? 50,
    )

    useEffect(() => {
        if (!isOpen) return
        setName('')
        setDescription('')
        setDataType(CUSTOM_FIELD_DATA_TYPES.Number)
        setError(null)
        latestMutationRequest.current++
        setSaving(false)
    }, [isOpen])

    const submit = async () => {
        if (!profileId || !name.trim()) return
        const requestedScope = mutationScope
        const request = ++latestMutationRequest.current
        const requestIsCurrent = () =>
            customFieldDefinitionMutationIsCurrent(
                request,
                latestMutationRequest.current,
                requestedScope,
                activeMutationScope.current,
            )
        setSaving(true)
        setError(null)
        try {
            await customFieldsApi.createDefinition(profileId, {
                entity_type: entityType,
                name: name.trim(),
                description: description.trim() || null,
                data_type: dataType,
            })
            if (requestIsCurrent()) {
                refreshQuota()
                onCreated()
                onClose()
            }
        } catch (requestError) {
            if (requestIsCurrent()) setError(customFieldApiError(requestError))
        } finally {
            if (requestIsCurrent()) setSaving(false)
        }
    }

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={onClose}
            title="Create custom field"
            width="default"
            dismissible={!saving}
            footer={
                <BulkActionDialogActions
                    primaryLabel="Create"
                    loadingLabel="Creating…"
                    loading={saving}
                    disabled={!profileId || !name.trim() || !!creationDisabledReason}
                    onPrimary={submit}
                    onCancel={onClose}
                />
            }
        >
            {!profileId && (
                <Callout intent="warning" title="Select an ad account" className="mb-[18px]" />
            )}
            {error && <Callout intent="danger" title={error} className="mb-[18px]" />}

            <BulkActionField label="Entity type" required>
                <Combobox
                    options={CUSTOM_FIELD_ENTITY_OPTIONS}
                    value={entityType}
                    onChange={(value) => setEntityType(value as CustomFieldEntityType)}
                    className="w-full"
                />
            </BulkActionField>
            <BulkActionField
                label="Name"
                htmlFor="custom-field-create-name"
                required
                description={`${catalog?.definition_count ?? 0}/${catalog?.definition_limit ?? 50} definitions · ${catalog?.value_count ?? 0}/${catalog?.value_limit ?? 100000} values for ${ENTITY_TYPE_LABELS[entityType]}`}
            >
                <input
                    id="custom-field-create-name"
                    className={fieldClass}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Field name"
                    maxLength={100}
                />
            </BulkActionField>
            <BulkActionField label="Description" htmlFor="custom-field-create-description">
                <input
                    id="custom-field-create-description"
                    className={fieldClass}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder="Description (optional)"
                    maxLength={1000}
                />
            </BulkActionField>
            <BulkActionField label="Type" htmlFor="custom-field-create-type">
                <select
                    id="custom-field-create-type"
                    className={fieldClass}
                    value={dataType}
                    onChange={(event) =>
                        setDataType(Number(event.target.value) as CustomFieldDataType)
                    }
                >
                    <option value={CUSTOM_FIELD_DATA_TYPES.Number}>Number</option>
                    <option value={CUSTOM_FIELD_DATA_TYPES.Boolean}>Boolean</option>
                    <option value={CUSTOM_FIELD_DATA_TYPES.String}>String</option>
                </select>
            </BulkActionField>
            {creationDisabledReason && (
                <Callout intent="warning" title="New field unavailable">
                    {creationDisabledReason}
                </Callout>
            )}
        </BulkActionDialog>
    )
}
